import { InterfaceLinkBrokenIcon } from '@calendly/ui/components/icons'

import { isValidUUID } from 'shared/utils/uuid'

import { ContactBadge } from '../contactBadge'
import { LinkBadge } from '../linkBadge'
import { MeetingBadge } from '../meetingBadge'
import { RecapBadge } from '../recapBadge'


/**
 * Configuration for app link protocol handlers
 */
const APP_LINK_HANDLERS = {
  'app://contact/': {
    component: ContactBadge,
    props: (id) => ({ contactId: id })
  },
  'app://recap/': {
    component: RecapBadge,
    props: (id, text) => ({ title: text, recapId: id })
  },
  'app://event/': {
    component: MeetingBadge,
    props: (id, text) => ({ title: text, meetingId: id })
  }
}

/**
 * Handles app:// protocol links by routing them to appropriate badge components
 * @param {string} href - The app:// link URL
 * @param {string} text - The display text for the link
 * @param {Object} [analyticsHandlers] - Analytics handlers for tracking events
 * @returns {React.ReactElement|null} A badge component or null for non-app links
 */
export const handleAppLink = (href, text, analyticsHandlers) => {
  // If it's not an app:// link, return null
  if (!href || !href.startsWith('app://')) {
    return null
  }

  // Find the matching protocol handler
  const matchedProtocol = Object.keys(APP_LINK_HANDLERS).find((protocol) => href.startsWith(protocol))

  // If it's an app:// link but doesn't match any known protocol, show broken link
  if (!matchedProtocol) {
    return (
      <LinkBadge
        text={text}
        disabled
        leftElement={<InterfaceLinkBrokenIcon />}
      />
    )
  }

  // Extract the ID from the href
  const id = href.replace(matchedProtocol, '')

  // Get the handler configuration
  const handler = APP_LINK_HANDLERS[matchedProtocol]
  const Component = handler.component
  const props = handler.props(id, text)

  // If the ID is not a valid UUID, show component with disabled state
  if (!isValidUUID(id)) {
    return <Component {...props} disabled analyticsHandlers={analyticsHandlers} />
  }

  return <Component {...props} analyticsHandlers={analyticsHandlers} />
}
